import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiPaths } from '../../../api/APIPaths';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { ICourse } from '../CoursesSlice';
import { ISection, TLessonTypes, TMeetingSectionPaymentType } from '.';
import { THomeworkFile, THomeworkQuiz } from '../HomeworksSlice';

const apiDataProvider = new APIDataProvider();

export type TCancelCourseZoomSectionAsUser = {
  _id: string;
  reason: string;
  trialCancelMainReason?: string;
  trialCancelSubReason?: string;
  trialCancelOtherText?: string;
};

export type TRescheduleCourseZoomSectionAsUser = {
  sectionId: string;
  reason: string;
  start: string;
  end: string;
  duration: number;
  title: string;
  isReschduleFreeSlot?: boolean;
  isConfirmed?: boolean;
  trialRescheduleMainReason?: string;
  trialRescheduleSubReason?: string;
  trialRescheduleOtherText?: string;
};

export type TRescheduleCourseTeamsSectionAsUser = {
  sectionId: string;
  reason: string;
  start: string;
  end: string;
  duration: number;
  title: string;
  isReschduleFreeSlot?: boolean;
  isConfirmed?: boolean;
  trialRescheduleMainReason?: string;
  trialRescheduleSubReason?: string;
  trialRescheduleOtherText?: string;
};

export type TCreateCourseHomeworkSectionAsUser = {
  parent?: string;
  sortingKey: number;
  course: string;
  title: string;
  dueDate: string;
  description: string;
  link?: string;
  files?: Array<THomeworkFile>;
  quizzes?: Array<THomeworkQuiz>;
};

export type TUpdateCourseHomeworkSectionAsUser = {
  _id: string;
  title: string;
  dueDate: string;
  description: string;
};

export type TCancelCourseTeamsSectionAsUser = {
  _id: string;
  reason: string;
  trialCancelMainReason?: string;
  trialCancelSubReason?: string;
  trialCancelOtherText?: string;
};

export type TBookCourseMeeting = {
  start: string;
  tutorId: string;
  courseId: string;
  duration: number;
};

export type TBookCourseMeetings = {
  /** The slot the student picked plus any weekly repeats they accepted. */
  starts: string[];
  tutorId: string;
  courseId: string;
  duration: number;
};

export type TBookCourseMeetingsResponse = {
  sections: ISection[];
  bookedStarts: string[];
  failedStarts: string[];
  /** Bookable balance after these lessons; null for students not on credits. */
  remainingMinutes: number | null;
};

export type TGetRecurringCourseMeetingSlots = {
  start: string;
  tutorId: string;
  courseId: string;
  duration: number;
};

/** Why the backend stopped offering further weekly slots. */
export type TRecurringCourseMeetingSlotsStopReason =
  | 'insufficient_credits'
  | 'enrolment_ending'
  | 'tutor_unavailable'
  | 'student_unavailable';

export type TGetRecurringCourseMeetingSlotsResponse = {
  /** ISO starts of the following weeks that are free for tutor and student. */
  slots: string[];
  /** Set when fewer weeks than the cap were offered; null when the cap was hit. */
  stopReason: TRecurringCourseMeetingSlotsStopReason | null;
};

export type TCreateNewCourseSectionAsUser = {
  sortingKey: number;
  course: string;
  title?: string;
  description?: string;
  parent?: string;
  template?: string;
};

export type TCreateCourseTeamSectionAsUser = {
  parent: string;
  sortingKey: number;
  course: string;
  title: string;
  dateTime: string;
  duration: number;
  template?: string;
  tutor: string;
  paymentType: TMeetingSectionPaymentType;
  lessonType: TLessonTypes;
};

export type TCreateCourseZoomSectionAsUser = {
  parent: string;
  sortingKey: number;
  course: string;
  title: string;
  dateTime: string;
  duration: number;
  template?: string;
  tutor: string;
  paymentType: TMeetingSectionPaymentType;
  lessonType: TLessonTypes;
};

export type TCreateCourseResourceSectionAsUser = {
  parent: string;
  sortingKey: number;
  course: string;
  curriculum: string;
  grade: string;
  subject: string;
  subjectCode: string;
  subjectLevel: string;
  quiztopiyaTopicId: string;
  quiztopiyaSubTopicId: string;
  quiztopiyaTopicName: string;
  quiztopiyaSubTopicName: string;
  template?: string;
};

export type TUpdateCourseSectionAsUser = {
  _id: string;
  course: string;
  title?: string;
  description?: string;
};

type TDeleteCourseSectionAsUser = {
  id: string;
  course: string;
};

export const cancelCourseTeamsSectionAsUser = createAsyncThunk(
  '/sections/cancelOneTeam',
  async (data: TCancelCourseTeamsSectionAsUser) => {
    const path = `${ApiPaths.teamMeetings}/cancel/${data._id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const rescheduleCourseTeamsSectionAsUser = createAsyncThunk(
  '/sections/rescheduleOneTeam',
  async (data: TRescheduleCourseTeamsSectionAsUser) => {
    const path = `${ApiPaths.teamMeetings}/reschedule/${data.sectionId}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const getUserCourseSectionsByCourseId = createAsyncThunk(
  '/courses/id/sections',
  async (id: string) => {
    const path = `${ApiPaths.courses}/${id}?subSections=1`;
    const response = await apiDataProvider.get<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

export const cancelCoursZoomeSectionAsUser = createAsyncThunk(
  '/sections/cancelOneZoom',
  async (data: TCancelCourseZoomSectionAsUser) => {
    const path = `${ApiPaths.zoomMeetings}/cancel/${data._id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const rescheduleCoursZoomeSectionAsUser = createAsyncThunk(
  '/sections/rescheduleOneZoom',
  async (data: TRescheduleCourseZoomSectionAsUser) => {
    const path = `${ApiPaths.zoomMeetings}/reschedule/${data.sectionId}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const getSectionByIdAsUser = createAsyncThunk(
  '/sections/getOne',
  async (data: { id: string }) => {
    const path = `${ApiPaths.sections}/${data.id}`;
    const response = await apiDataProvider.get<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const createCourseHomeworkSectionAsUser = createAsyncThunk(
  '/sections/createHomework',
  async (data: TCreateCourseHomeworkSectionAsUser) => {
    const path = ApiPaths.homeworks;
    const response = await apiDataProvider.post<
      TApiResponse<{ section: ISection; parentSection: ISection }>
    >(path, data);

    return response.data;
  }
);

export const updateCourseHomeworkSectionAsUser = createAsyncThunk(
  '/sections/updateHomework',
  async ({ _id, ...data }: TUpdateCourseHomeworkSectionAsUser) => {
    const path = `${ApiPaths.homeworks}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const bookCourseMeeting = createAsyncThunk(
  '/team-meetings/course-meeting-book',
  async (data: TBookCourseMeeting) => {
    const path = `${ApiPaths.teamMeetings}/course-meeting-book`;
    const response = await apiDataProvider.post<TApiResponse<ISection[]>>(
      path,
      data
    );

    return response.data;
  }
);

export const bookCourseMeetings = createAsyncThunk(
  '/team-meetings/course-meeting-book-multiple',
  async (data: TBookCourseMeetings) => {
    const path = `${ApiPaths.teamMeetings}/course-meeting-book-multiple`;
    const response = await apiDataProvider.post<
      TApiResponse<TBookCourseMeetingsResponse>
    >(path, data);

    return response.data;
  }
);

export const getRecurringCourseMeetingSlots = createAsyncThunk(
  '/team-meetings/course-meeting-recurring-slots',
  async (data: TGetRecurringCourseMeetingSlots) => {
    const path = `${ApiPaths.teamMeetings}/course-meeting-recurring-slots`;
    const response = await apiDataProvider.post<
      TApiResponse<TGetRecurringCourseMeetingSlotsResponse>
    >(path, data);

    return response.data;
  }
);

const createNewCourseSectionAsUserModule = async (
  data: TCreateNewCourseSectionAsUser
) => {
  const path = `${ApiPaths.sections}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const createNewCourseSectionAsUser = createAsyncThunk(
  '/sections/create',
  createNewCourseSectionAsUserModule
);

const createNewCourseTeamSectionAsUserModule = async (
  data: TCreateCourseTeamSectionAsUser
) => {
  const path = `${ApiPaths.teamMeetings}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const createNewCourseTeamSectionAsUser = createAsyncThunk(
  '/sections/createTeam',
  createNewCourseTeamSectionAsUserModule
);

const createNewCourseZoomSectionAsUserModule = async (
  data: TCreateCourseZoomSectionAsUser
) => {
  const path = `${ApiPaths.zoomMeetings}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const createNewCourseZoomSectionAsUser = createAsyncThunk(
  '/sections/createZoom',
  createNewCourseZoomSectionAsUserModule
);

const createNewCourseResourceSectionAsUserModule = async (
  data: TCreateCourseResourceSectionAsUser
) => {
  const path = `${ApiPaths.resources}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const createNewCourseResourceSectionAsUser = createAsyncThunk(
  '/sections/createResource',
  createNewCourseResourceSectionAsUserModule
);

const updateCourseSectionAsUserModule = async (
  data: TUpdateCourseSectionAsUser
) => {
  const path = `${ApiPaths.sections}/${data._id}`;
  const response = await apiDataProvider.put<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const updateCourseSectionAsUser = createAsyncThunk(
  '/sections/updateOne',
  updateCourseSectionAsUserModule
);

export const deleteCourseSectionByIdAsUser = createAsyncThunk(
  '/sections/deleteOne',
  async ({ id, course }: TDeleteCourseSectionAsUser) => {
    const path = `${ApiPaths.sections}/${id}?course=${course}`;
    const response = await apiDataProvider.delete<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const deleteCourseTeamSectionByIdAsUser = createAsyncThunk(
  '/sections/deleteOneTeam',
  async (data: { id: string; course: string }) => {
    const path = `${ApiPaths.teamMeetings}/delete/${data.id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteCourseZoomSectionByIdAsUser = createAsyncThunk(
  '/sections/deleteOneZoom',
  async (data: { id: string; course: string }) => {
    const path = `${ApiPaths.zoomMeetings}/delete/${data.id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);
